import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './ParametrosLaboralesModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Parámetros Laborales";

const endpoint = ENDPOINTS.parametrosLaborales;
const endpointById = ENDPOINTS.parametrosLaboralesById;
const modelo = Models.parametrosLaborales;

// Catálogo de Unidades Organizacionales para mostrar nombre en lugar de ID
const endpointUnidadesOrg = ENDPOINTS.unidadesOrg;
const modeloUnidadesOrg = Models.unidadesOrg;

// 🔹 Endpoints adicionales para recálculo de reservas
const endpointReservas = ENDPOINTS.reserva;
const modeloReservas = Models.reserva;
const endpointChoferes = ENDPOINTS.chofer;
const modeloChofer = Models.chofer;
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;
const endpointUnidadesOrgEmpleado = ENDPOINTS.unidadesOrgEmpleado;
const modeloUnidadesOrgEmpleado = Models.unidadOrgEmpleado;
const endpointExtrasMes = ENDPOINTS.extrasAcumuladoMes;
const modeloExtrasMes = Models.extrasAcumuladoMes;
const endpointExtrasSemana = ENDPOINTS.extrasAcumuladoSemana;
const modeloExtrasSemana = Models.extrasAcumuladoSemana;
const endpointTurnos = ENDPOINTS.turnos;
const modeloTurnos = Models.turnos;
const endpointEmpleadoTurno = ENDPOINTS.empleadosTurnos;
const modeloEmpleadoTurnos = Models.empleadosTurnos;

// Constantes de tipos de aprobación
const TIPO_APROBACION = {
  FUERA_HORARIO: 1,
  HORAS_SEMANA_EXCEDIDAS: 2,
  HORAS_MES_EXCEDIDAS: 3,
  FIN_SEMANA: 4,
  DIAS_FESTIVOS: 5,
  VARIOS_DIAS: 6,
  GENERAL: 9,
};

const maps = {
  mapCreate: (f) => ({
    idUnidadOrganizacional: f.idUnidadOrganizacional ? Number(f.idUnidadOrganizacional) : null,
    topeSemanalMin: Number(f.topeSemanalMin) || 0,
    topeMensualMin: Number(f.topeMensualMin) || 0,
    // Las vigencias no se utilizan; se envían como indefinidas
    vigenciaDesde: undefined,
    vigenciaHasta: undefined,
  }),
  mapUpdate: (id, f) => ({
    idParametro: id,
    idUnidadOrganizacional: f.idUnidadOrganizacional ? Number(f.idUnidadOrganizacional) : null,
    topeSemanalMin: Number(f.topeSemanalMin) || 0,
    topeMensualMin: Number(f.topeMensualMin) || 0,
    // Las vigencias no se utilizan; se envían como indefinidas
    vigenciaDesde: undefined,
    vigenciaHasta: undefined,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [showHijas, setShowHijas] = useState(false);

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Parámetros laborales + catálogo de Unidades Organizacionales
      const [resParams, resUnidadesOrg] = await Promise.all([
        axios.get(ENDPOINTS.parametrosLaboralesSecurity(showHijas ? 'hijas' : 'propias')),
        axios.get(endpointUnidadesOrg),
      ]);

      let paramsNorm = (resParams.data || []).map((x) => modelo(x));
      const unidadesNorm = (resUnidadesOrg.data || []).map((x) =>
        modeloUnidadesOrg(x)
      );

      // Enriquecer cada parámetro con nombreUnidadOrg
      paramsNorm = paramsNorm.map((p) => {
        const uo = unidadesNorm.find(
          (u) => u.idUnidadOrganizacional === p.idUnidadOrganizacional
        );

        const baseNombre =
          (uo &&
            (
              uo.nombreUnidadOrganizacional ||
              uo.nombreUnidadOrg ||
              uo.nombre ||
              uo.descripcion ||
              ''
            ).toString()) ||
          '';

        const claveUnidadOrg = uo?.clave || '';
        const nombreUnidadOrg = baseNombre || `Unidad #${p.idUnidadOrganizacional ?? ''}`;

        return {
          ...p,
          nombreUnidadOrg,
          claveUnidadOrg,
          nombreEmpresa: uo?.nombreEmpresa || 'Global',
        };
      });

      setRows(paramsNorm);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [showHijas]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const idUnidad = String(r.idUnidadOrganizacional ?? '').toLowerCase();
      const nombreUo = (r.nombreUnidadOrg || '').toLowerCase();
      const claveUo = (r.claveUnidadOrg || '').toLowerCase();
      const semanal = String(r.topeSemanalMin ?? '').toLowerCase();
      const mensual = String(r.topeMensualMin ?? '').toLowerCase();
      const activo = r.activo ? 'si' : 'no';

      return (
        idUnidad.includes(q) ||
        nombreUo.includes(q) ||
        claveUo.includes(q) ||
        semanal.includes(q) ||
        mensual.includes(q) ||
        activo.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find((x) => x.idParametro === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  // 🔹 ========== FUNCIONES HELPER PARA RECÁLCULO ==========

  const getISOWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const toMinutes = (hm) => {
    const [h, m] = String(hm).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const computeNetExtraMinutes = (startHM, endHM, intervals) => {
    const startMin = toMinutes(startHM);
    const endMin = toMinutes(endHM);
    const totalMin = endMin - startMin;
    if (totalMin <= 0 || !intervals || intervals.length === 0) return totalMin;

    let covered = 0;
    intervals.forEach(iv => {
      const ivStart = toMinutes(iv.start);
      const ivEnd = toMinutes(iv.end);
      const overlapStart = Math.max(startMin, ivStart);
      const overlapEnd = Math.min(endMin, ivEnd);
      if (overlapEnd > overlapStart) covered += (overlapEnd - overlapStart);
    });

    return Math.max(0, totalMin - covered);
  };

  // 🔹 ========== FUNCIÓN PRINCIPAL DE RECÁLCULO ==========

  const recalcularReservasPendientes = async (idUnidadOrganizacional) => {
    console.log(`\n========================================`);
    console.log(`[ParametrosLaborales] 🔄 INICIANDO RECÁLCULO DE RESERVAS`);
    console.log(`[ParametrosLaborales] UO ID: ${idUnidadOrganizacional}`);
    console.log(`========================================\n`);

    try {
      // 1. Cargar todos los datos necesarios
      console.log(`[ParametrosLaborales] 📥 Cargando datos...`);
      const [
        resReservas,
        resChoferes,
        resEmpleados,
        resUnidadesOrgEmpleado,
        resParametros,
        resExtrasMes,
        resExtrasSemana,
        resTurnos,
        resEmpleadosTurnos,
      ] = await Promise.all([
        axios.get(endpointReservas),
        axios.get(endpointChoferes),
        axios.get(endpointEmpleado),
        axios.get(endpointUnidadesOrgEmpleado),
        axios.get(endpoint), // Parámetros laborales actualizados
        axios.get(endpointExtrasMes),
        axios.get(endpointExtrasSemana),
        axios.get(endpointTurnos),
        axios.get(endpointEmpleadoTurno),
      ]);

      const reservas = (resReservas.data || []).map(modeloReservas);
      const choferes = (resChoferes.data || []).map(modeloChofer);
      const empleados = (resEmpleados.data || []).map(modeloEmpleado);
      const unidadesOrgEmpleado = (resUnidadesOrgEmpleado.data || []).map(modeloUnidadesOrgEmpleado);
      const parametros = (resParametros.data || []).map(modelo);
      const extrasMes = (resExtrasMes.data || []).map(modeloExtrasMes);
      const extrasSemana = (resExtrasSemana.data || []).map(modeloExtrasSemana);
      const turnos = (resTurnos.data || []).map(modeloTurnos);
      const empleadosTurnos = (resEmpleadosTurnos.data || []).map(modeloEmpleadoTurnos);

      console.log(`[ParametrosLaborales] ✅ Datos cargados`);

      // 2. Encontrar parámetro laboral de esta UO
      const param = parametros.find(p => p.idUnidadOrganizacional === idUnidadOrganizacional);
      if (!param) {
        console.log(`[ParametrosLaborales] ⚠️ No se encontró parámetro para UO ${idUnidadOrganizacional}`);
        return;
      }

      const limiteSemanalHoras = Number(param.topeSemanalMin) > 0 ? Number(param.topeSemanalMin) : null;
      const limiteMensualHoras = Number(param.topeMensualMin) > 0 ? Number(param.topeMensualMin) : null;

      console.log(`[ParametrosLaborales] Límites: Semanal=${limiteSemanalHoras}h, Mensual=${limiteMensualHoras}h`);

      // 3. Filtrar choferes de esta UO
      const choferesDeUO = choferes.filter(ch => {
        const emp = empleados.find(e => e.idEmpleado === ch.idEmpleado);
        if (!emp) return false;
        const uoRels = unidadesOrgEmpleado.filter(rel => rel.idEmpleado === emp.idEmpleado);
        return uoRels.some(rel => rel.idUnidadOrganizacional === idUnidadOrganizacional);
      });

      console.log(`[ParametrosLaborales] Choferes de UO: ${choferesDeUO.length}`);

      if (choferesDeUO.length === 0) {
        console.log(`[ParametrosLaborales] No hay choferes en esta UO`);
        return;
      }

      const choferIds = choferesDeUO.map(ch => ch.idChofer);

      // 4. Filtrar reservas PENDIENTES de estos choferes
      const reservasPendientes = reservas.filter(r =>
        Number(r.idEstadoReserva) === 1 && choferIds.includes(r.idChofer)
      );

      console.log(`[ParametrosLaborales] 📋 Reservas pendientes encontradas: ${reservasPendientes.length}`);

      if (reservasPendientes.length === 0) {
        console.log(`[ParametrosLaborales] No hay reservas pendientes para recalcular`);
        return;
      }

      let updatesMade = false;

      // 5. Para cada reserva pendiente, recalcular su tipo
      for (const reserva of reservasPendientes) {
        console.log(`\n[ParametrosLaborales] 🔍 Analizando reserva #${reserva.idReserva}...`);

        const tipoActual = reserva.idTipoAprobacion;

        // Solo recalcular tipos relacionados con horas
        const tiposRecalculables = [
          TIPO_APROBACION.FUERA_HORARIO,
          TIPO_APROBACION.HORAS_SEMANA_EXCEDIDAS,
          TIPO_APROBACION.HORAS_MES_EXCEDIDAS,
        ];

        if (!tiposRecalculables.includes(tipoActual)) {
          console.log(`[ParametrosLaborales]   Tipo ${tipoActual} no es recalculable, saltando...`);
          continue;
        }

        // Calcular horas netas de esta reserva
        const chofer = choferes.find(ch => ch.idChofer === reserva.idChofer);
        if (!chofer) continue;

        const asignaciones = empleadosTurnos.filter(et => et.idEmpleado === chofer.idEmpleado);
        const turnoIds = [...new Set(asignaciones.map(a => a.idTurno).filter(Boolean))];
        const intervals = turnoIds
          .map(idT => turnos.find(t => t.idTurno === idT))
          .filter(Boolean)
          .map(t => ({
            start: String(t.horaInicio || '').slice(0, 5),
            end: String(t.horaFin || '').slice(0, 5),
          }));

        const horaInicio = String(reserva.horaInicio || '').slice(0, 5);
        const horaFin = String(reserva.horaFin || '').slice(0, 5);
        const extraMinutes = computeNetExtraMinutes(horaInicio, horaFin, intervals);
        const horasExtraNetas = Math.floor(extraMinutes / 60);

        if (horasExtraNetas <= 0) {
          console.log(`[ParametrosLaborales]   Sin horas extra netas, saltando...`);
          continue;
        }

        // Calcular acumulados
        const fechaInicio = new Date(reserva.fechaInicio);
        const year = fechaInicio.getFullYear();
        const month = fechaInicio.getMonth() + 1;
        const week = getISOWeek(fechaInicio);

        const rowsMes = extrasMes.filter(row =>
          row.idChofer === reserva.idChofer &&
          Number(row.anio) === year &&
          Number(row.mes) === month
        );
        const totalMes = rowsMes.reduce((acc, row) => acc + (Number(row.horasExtras) || 0), 0);

        const rowsSemana = extrasSemana.filter(row =>
          row.idChofer === reserva.idChofer &&
          Number(row.anio) === year &&
          Number(row.semana) === week
        );
        const totalSemana = rowsSemana.reduce((acc, row) => acc + (Number(row.horasExtras) || 0), 0);

        // Calcular nuevo tipo
        let nuevoTipo = TIPO_APROBACION.FUERA_HORARIO;

        const totalConActualMes = totalMes + horasExtraNetas;
        const totalConActualSemana = totalSemana + horasExtraNetas;

        if (limiteMensualHoras != null && totalConActualMes > limiteMensualHoras) {
          nuevoTipo = TIPO_APROBACION.HORAS_MES_EXCEDIDAS;
        } else if (limiteSemanalHoras != null && totalConActualSemana > limiteSemanalHoras) {
          nuevoTipo = TIPO_APROBACION.HORAS_SEMANA_EXCEDIDAS;
        }

        console.log(`[ParametrosLaborales]   Tipo actual: ${tipoActual}, Tipo calculado: ${nuevoTipo}`);
        console.log(`[ParametrosLaborales]   Total semana: ${totalConActualSemana}h, Total mes: ${totalConActualMes}h`);

        // Si cambió, actualizar
        if (nuevoTipo !== tipoActual) {
          console.log(`[ParametrosLaborales]   ⚠️ TIPO CAMBIÓ: ${tipoActual} → ${nuevoTipo}`);

          try {
            const updatePayload = {
              idReserva: reserva.idReserva,
              idSolicitante: reserva.idSolicitante,
              idChofer: reserva.idChofer,
              fechaInicio: reserva.fechaInicio,
              fechaFin: reserva.fechaFin,
              horaInicio: reserva.horaInicio,
              horaFin: reserva.horaFin,
              origenTexto: reserva.origenTexto || '',
              destinoTexto: reserva.destinoTexto || '',
              motivo: reserva.motivo || '',
              observaciones: reserva.observaciones || '',
              idEstadoReserva: 1, // Mantener PENDIENTE
              fueraHorarioMin: reserva.fueraHorarioMin || false,
              idTipoAprobacion: nuevoTipo, // 🎯 ACTUALIZAR
            };

            await axios.put(endpointReservas, updatePayload);
            updatesMade = true;
            console.log(`[ParametrosLaborales]   ✅ Reserva #${reserva.idReserva} actualizada`);
          } catch (updateErr) {
            console.error(`[ParametrosLaborales]   ❌ ERROR actualizando reserva #${reserva.idReserva}:`, updateErr);
          }
        } else {
          console.log(`[ParametrosLaborales]   ℹ️ No requiere actualización`);
        }
      }

      console.log(`\n========================================`);
      console.log(`[ParametrosLaborales] ✅ RECÁLCULO COMPLETADO`);
      console.log(`[ParametrosLaborales] Actualizaciones realizadas: ${updatesMade ? 'SÍ' : 'NO'}`);
      console.log(`========================================\n`);

    } catch (err) {
      console.error('[ParametrosLaborales] ❌ Error en recálculo:', err);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      const idUO = payload.idUnidadOrganizacional;

      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId =
          res?.data?.idParametro ??
          (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }

      setModalOpen(false);
      await loadData();

      // 🔹 Recalcular reservas pendientes de esta UO
      if (idUO) {
        console.log(`[ParametrosLaborales] Iniciando recálculo para UO ${idUO}...`);
        await recalcularReservasPendientes(idUO);
      }

    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por unidad org., topes o activo…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                  className={`btn-filter ${showHijas ? 'active' : ''}`}
                  onClick={() => setShowHijas(!showHijas)}
                  title={showHijas ? "Ocultar parámetros de empresas hijas" : "Ver parámetros de empresas hijas"}
              >
                  {showHijas ? '👁️ Ver Propias' : '🌿 Ver Hijas'}
              </button>
              {puedeCrear && (
                <button className="btn-action" onClick={handleNew}>
                  ＋ Nuevo
                </button>
              )}
          </div>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Clave Unidad</th>
                  <th>Unidad Organizacional</th>
                  <th>Tope semanal (Horas)</th>
                  <th>Tope mensual (Horas)</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idParametro}>
                    <td>
                      <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                        {normalizeValue(r.nombreEmpresa)}
                      </span>
                    </td>
                    <td>{normalizeValue(r.claveUnidadOrg)}</td>
                    <td>{normalizeValue(r.nombreUnidadOrg)}</td>
                    <td>{normalizeValue(r.topeSemanalMin)} hrs</td>
                    <td>{normalizeValue(r.topeMensualMin)} hrs</td>

                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idParametro)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}

                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            const nombreUo =
                              (r.nombreUnidadOrg && r.nombreUnidadOrg.trim()) ||
                              `Unidad ${r.idUnidadOrganizacional ?? ''}`;
                            askDelete(r.idParametro, nombreUo || `Parámetro ${r.idParametro}`);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan={5 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>
                Mostrando {startIndex}–{endIndex} de {filtered.length}
              </span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
